import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Building2, Monitor } from 'lucide-react';
import useAuthStore, { PERMISSIONS } from '../stores/authStore';
import api from '../api/client';
import PCActionModal from '../components/PCActionModal';
import { formatPcHistory, formatRegDate } from '../utils/registrationDate';
import { LIFECYCLE_BADGE } from '../components/cafeLifecycleBadge';

/**
 * A café's Remote access page (/cafes/:id/remote): its PCs as live tiles, each
 * opening the command panel. Everything else about the café — owner, history,
 * revenue, archive/edit — is on its main page (/cafes/:id, CafePcDetails.jsx).
 */

// Mirrors primus-admin-main's PCManagement.jsx pcVisualState(): a customer
// actively playing (current_user_id set while online) gets the green
// "in-use" tile; powered on with nobody signed in (idle at the login
// screen, or admin-locked) gets amber; anything not seen recently is red.
const pcVisualState = (pc) => {
    if (!pc.online) return 'is-offline';
    if (pc.current_user_id) return 'is-inuse';
    return 'is-locked';
};

// The chips above the tiles, keyed like pcVisualState's three states.
const filterKeyOf = (pc) => {
    if (!pc.online) return 'offline';
    return pc.current_user_id ? 'inuse' : 'idle';
};
const FILTERS = [
    { key: 'inuse', label: 'in use', dot: 'inuse' },
    { key: 'idle', label: 'idle', dot: 'idle' },
    { key: 'offline', label: 'offline', dot: 'offline' },
    { key: 'all', label: 'total' },
];

// Backend sends naive UTC timestamps (no 'Z'); JS Date() would read them as
// local time and skew "X ago". Normalise to UTC before parsing.
const parseUtc = (iso) => {
    if (!iso) return null;
    const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
    return new Date(hasTz ? iso : `${iso}Z`);
};

// A fresh page per café: the owner's-cafés switcher changes :id in place, and
// keying on it resets the chip filter and anything else from the café just left.
export default function CafeRemotePage() {
    const { id } = useParams();
    return <CafeRemote key={id} />;
}

function CafeRemote() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = useAuthStore();

    const [cafe, setCafe] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPC, setSelectedPC] = useState(null);
    const [showCommandModal, setShowCommandModal] = useState(false);
    const [draggedPcId, setDraggedPcId] = useState(null);
    const [dragOverPcId, setDragOverPcId] = useState(null);
    const [reorderError, setReorderError] = useState(null);
    // 'all' | 'inuse' | 'idle' | 'offline' — the chips above the tiles.
    const [filter, setFilter] = useState('all');

    const canEdit = hasPermission(PERMISSIONS.EDIT_CAFE_DETAILS) || isSuperAdmin();
    // Reordering only makes sense with every tile on show: in a filtered view
    // a swap would move tiles the person can't see.
    const canDrag = canEdit && filter === 'all';
    const canRemoteAccess = hasPermission(PERMISSIONS.REMOTE_PC_ACCESS) || isSuperAdmin();
    // Opening a PC's panel is a view: its buttons each check their own right, so
    // a view-only account sees the PC (and its chat) with every action greyed out.
    const canOpenPC = canRemoteAccess || hasPermission(PERMISSIONS.VIEW_PC_HEALTH);

    const loadCafe = useCallback(() => {
        return api.get(`/internal/cafes/${id}`).then((res) => setCafe(res.data));
    }, [id]);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);
        loadCafe()
            .catch((err) => {
                console.error('Failed to load café:', err);
                if (!cancelled) setError(err.response?.data?.detail || 'Failed to load café');
            })
            .finally(() => { if (!cancelled) setIsLoading(false); });
        return () => { cancelled = true; };
    }, [id, loadCafe]);

    const formatTimeAgo = (iso) => {
        const d = parseUtc(iso);
        if (!d) return 'never';
        const s = Math.floor((Date.now() - d.getTime()) / 1000);
        if (s < 0) return 'just now';
        if (s < 60) return `${s}s ago`;
        if (s < 3600) return `${Math.floor(s / 60)}m ago`;
        if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
        return `${Math.floor(s / 86400)}d ago`;
    };

    const handlePCClick = (pc) => {
        // Matches primus-admin-main's PCManagement.jsx: the tile always opens
        // the deck regardless of online status -- only individual actions that
        // need a live device (lock/unlock/reboot/etc, not rename/remove) are
        // disabled per-button when the PC is offline.
        if (canOpenPC) {
            setSelectedPC(pc);
            setShowCommandModal(true);
        }
    };

    const closeCommandModal = () => {
        setShowCommandModal(false);
        setSelectedPC(null);
    };

    // PATCH /internal/pcs/{cafe_id}/reorder { pc_ids: [...] } -- persists a
    // full new card order. Dropping card A onto card B swaps their two
    // positions (not a shift of everything in between), matching the drag
    // gesture on the grid below.
    const handleDragStart = (pcId) => {
        if (!canDrag) return;
        setDraggedPcId(pcId);
    };

    const handleDragOver = (e, pcId) => {
        if (!canDrag || draggedPcId == null) return;
        e.preventDefault();
        if (pcId !== dragOverPcId) setDragOverPcId(pcId);
    };

    const handleDragEnd = () => {
        setDraggedPcId(null);
        setDragOverPcId(null);
    };

    const handleDrop = async (targetPcId) => {
        const sourcePcId = draggedPcId;
        setDraggedPcId(null);
        setDragOverPcId(null);
        if (!canDrag || sourcePcId == null || sourcePcId === targetPcId) return;

        const sourcePc = sortedPcs.find((p) => p.id === sourcePcId);
        const targetPc = sortedPcs.find((p) => p.id === targetPcId);
        if (!sourcePc || !targetPc) return;

        const sourcePos = sourcePc.position ?? sortedPcs.indexOf(sourcePc);
        const targetPos = targetPc.position ?? sortedPcs.indexOf(targetPc);
        const swapped = pcs.map((pc) => {
            if (pc.id === sourcePcId) return { ...pc, position: targetPos };
            if (pc.id === targetPcId) return { ...pc, position: sourcePos };
            return pc;
        });

        setReorderError(null);
        setCafe((prev) => (prev ? { ...prev, pcs: swapped } : prev));

        const orderedIds = [...swapped]
            .sort((a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER) || a.id - b.id)
            .map((pc) => pc.id);
        try {
            await api.patch(`/internal/pcs/${id}/reorder`, { pc_ids: orderedIds });
        } catch (err) {
            console.error('Failed to save PC order:', err);
            setReorderError(err.response?.data?.detail || 'Failed to save new PC order.');
            await loadCafe();
        }
    };

    if (isLoading) return <div className="loading-state"><div className="spinner" /></div>;

    if (error || !cafe) return (
        <div className="empty-state">
            <Building2 size={48} />
            <p>{error || 'Café not found'}</p>
            <button className="btn btn--secondary" onClick={() => navigate(`/cafes/${id}`)}>Back to café</button>
        </div>
    );

    const pcs = cafe.pcs || [];
    // Drag-to-reorder persists via PC.position (nulls-last, then id) -- see
    // handleDrop below and PATCH /internal/pcs/{cafe_id}/reorder.
    const sortedPcs = [...pcs].sort((a, b) => {
        const posA = a.position ?? Number.MAX_SAFE_INTEGER;
        const posB = b.position ?? Number.MAX_SAFE_INTEGER;
        return posA - posB || a.id - b.id;
    });
    // The same three states as the tiles, so the chips and the grid agree.
    const pcStats = {
        inuse: pcs.filter((p) => filterKeyOf(p) === 'inuse').length,
        idle: pcs.filter((p) => filterKeyOf(p) === 'idle').length,
        offline: pcs.filter((p) => filterKeyOf(p) === 'offline').length,
    };
    const visiblePcs = filter === 'all' ? sortedPcs : sortedPcs.filter((p) => filterKeyOf(p) === filter);
    const lifecycle = LIFECYCLE_BADGE[cafe.status];
    // Every café this owner owns, this one included — backs the switcher below.
    const ownerCafes = cafe.owner_cafes || [];

    return (
        <div className="cafe-detail">
            <Link to={`/cafes/${id}`} className="back-link"><ArrowLeft size={16} /><span>Back to {cafe.name}</span></Link>

            <div className="cafe-header">
                <div className="cafe-header__main">
                    <h1 className="cafe-header__title">
                        {cafe.name} <span className="cafe-header__page">· Remote access</span>
                    </h1>
                    {lifecycle && <span className={`badge badge--${lifecycle.color}`}>{lifecycle.label}</span>}
                </div>
                {/* Switching keeps you on Remote access for the other café. */}
                {ownerCafes.length > 1 && (
                    <label className="owner-cafes">
                        <span className="owner-cafes__label">Owner&apos;s cafés ({ownerCafes.length})</span>
                        <select
                            className="field__select owner-cafes__select"
                            value={cafe.id}
                            onChange={(e) => navigate(`/cafes/${e.target.value}/remote`)}
                        >
                            {ownerCafes.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}{c.location ? ` — ${c.location}` : ''}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
            </div>

            <div className="pcs-header">
                {/* Click a chip to show only those PCs; again (or Total) for all. */}
                <div className="pcs-chips">
                    {FILTERS.map((f) => {
                        const on = filter === f.key;
                        const n = f.key === 'all' ? pcs.length : pcStats[f.key];
                        return (
                            <button
                                key={f.key}
                                type="button"
                                className={`pcs-chip${on ? ' pcs-chip--on' : ''}`}
                                aria-pressed={on}
                                onClick={() => setFilter(on && f.key !== 'all' ? 'all' : f.key)}
                            >
                                {f.dot && <span className={`pcs-dot pcs-dot--${f.dot}`} />}
                                <b>{n}</b> {f.label}
                            </button>
                        );
                    })}
                </div>
                {pcs.length > 0 && (
                    <span className="pcs-header__hint">
                        {canOpenPC ? 'Click a PC for commands' : null}
                        {canOpenPC && canEdit ? ' · ' : null}
                        {canEdit ? (canDrag ? 'drag to reorder' : 'show Total to reorder') : null}
                    </span>
                )}
            </div>

            {reorderError && <p className="modal-error">{reorderError}</p>}

            {pcs.length === 0 ? (
                <div className="empty-state">
                    <Monitor size={40} />
                    <p>No PCs registered for this café yet.</p>
                </div>
            ) : visiblePcs.length === 0 ? (
                <div className="empty-state">
                    <Monitor size={40} />
                    <p>No PCs here.</p>
                </div>
            ) : (
            <div className="pcgrid_compact">
                {visiblePcs.map((pc) => {
                    const state = pcVisualState(pc);
                    return (
                        <button
                            key={pc.id}
                            type="button"
                            className={`ctile ${state}${draggedPcId === pc.id ? ' is-dragging' : ''}${dragOverPcId === pc.id && draggedPcId !== null && draggedPcId !== pc.id ? ' is-dragover' : ''}`}
                            onClick={() => handlePCClick(pc)}
                            disabled={!canOpenPC}
                            draggable={canDrag}
                            onDragStart={() => handleDragStart(pc.id)}
                            onDragOver={(e) => handleDragOver(e, pc.id)}
                            onDragLeave={() => setDragOverPcId((prev) => (prev === pc.id ? null : prev))}
                            onDrop={(e) => { e.preventDefault(); handleDrop(pc.id); }}
                            onDragEnd={handleDragEnd}
                            title={[
                                `${pc.name} — ${pc.online ? (pc.current_user_id ? (typeof pc.remaining_time === 'number' ? `In use · ${pc.remaining_time} min left` : 'In use') : 'Idle') : 'Offline'}`,
                                `Registered ${formatRegDate(pc.registered_at, pc.registered_at_approx)}`,
                                formatPcHistory(pc),
                                canDrag ? 'Drag to reorder' : null,
                            ].filter(Boolean).join('\n')}
                        >
                            <span className="ctile_screen">
                                <svg className="ctile_mon" viewBox="0 0 80 64" aria-hidden="true">
                                    <rect className="ctile_bezel" x="6" y="4" width="68" height="46" rx="6" />
                                    <rect className="ctile_glass" x="11" y="9" width="58" height="36" rx="3" />
                                    <rect className="ctile_stand" x="35" y="50" width="10" height="7" />
                                    <path className="ctile_base" d="M24 61 L56 61 L50 56 L30 56 Z" />
                                </svg>
                                <span className="ctile_ping" />
                            </span>
                            <span className="ctile_name">{pc.name}</span>
                            <span className="ctile_meta">
                                {pc.online
                                    ? (pc.current_user_id
                                        ? (typeof pc.remaining_time === 'number' ? `${pc.remaining_time} min left` : 'In use')
                                        : 'Idle')
                                    : formatTimeAgo(pc.last_seen)}
                            </span>
                        </button>
                    );
                })}
            </div>
            )}

            {showCommandModal && selectedPC && (
                <PCActionModal
                    pc={selectedPC}
                    cafeId={Number(id)}
                    onClose={closeCommandModal}
                    onChanged={loadCafe}
                />
            )}

            <style>{`
        .cafe-detail {
          /* noLag rebrand: scoped to this page only. Deliberately does NOT
             touch .ctile / --scr / --scrglow below — that PC tile grid is
             built to match primus-admin-main's PCManagement.jsx pixel-for-
             pixel (see the comment on .pcgrid_compact) so a PC looks the
             same in both apps; its green/amber/red states are functional,
             not brand color. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);

          max-width: 1200px;
        }
        .loading-state { display: flex; align-items: center; justify-content: center; min-height: 50vh; }
        .back-link {
          display: inline-flex; align-items: center; gap: var(--space-2);
          color: var(--text-secondary); font-size: var(--text-sm);
          margin-bottom: var(--space-6); transition: color var(--duration-fast) var(--ease-out);
        }
        .back-link:hover { color: var(--accent-primary); }

        .cafe-header {
          display: flex; align-items: center; justify-content: space-between; gap: var(--space-4);
          flex-wrap: wrap; margin-bottom: var(--space-3);
        }
        .cafe-header__main { display: flex; align-items: center; gap: var(--space-4); flex-wrap: wrap; }
        .cafe-header__title { font-size: var(--text-2xl); font-weight: 600; letter-spacing: -0.02em; margin: 0; }
        .cafe-header__page { font-weight: 400; color: var(--text-tertiary); }
        .owner-cafes { display: flex; align-items: center; gap: var(--space-3); }
        .owner-cafes__label { font-size: var(--text-xs); color: var(--text-tertiary); white-space: nowrap; }
        .owner-cafes__select { width: auto; min-width: 240px; max-width: 100%; }

        .pcs-header {
          display: flex; align-items: center; justify-content: space-between; gap: var(--space-4);
          flex-wrap: wrap; margin-bottom: var(--space-6);
        }
        .pcs-chips { display: flex; flex-wrap: wrap; gap: var(--space-2); }
        .pcs-chip {
          display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px;
          border: 1px solid rgba(255, 255, 255, 0.16); border-radius: var(--radius-md); background: rgba(255, 255, 255, 0.06);
          font: inherit; font-size: var(--text-sm); color: var(--text-secondary); cursor: pointer;
          transition: all var(--duration-fast) var(--ease-out);
        }
        .pcs-chip b { color: var(--text-primary); font-weight: 600; }
        .pcs-chip:hover { border-color: rgba(255, 255, 255, 0.32); }
        .pcs-chip--on { border-color: var(--accent-primary); background: var(--accent-primary-subtle); }
        .pcs-header__hint { font-size: var(--text-xs); color: var(--text-tertiary); }
        /* The tiles' own colours (see .ctile states below). */
        .pcs-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; }
        .pcs-dot--inuse { background: #16e07a; }
        .pcs-dot--idle { background: #eab308; }
        .pcs-dot--offline { background: #f43f5e; }

        /* Compact tile grid -- matches primus-admin-main's PCManagement.jsx
           .ctile design exactly (same monitor SVG, same state colors) so a PC
           looks the same whether viewed from the café's own admin app or here. */
        .pcgrid_compact { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 16px; }
        .ctile {
          position: relative; display: flex; flex-direction: column; align-items: center; gap: 6px;
          padding: 16px 12px 14px; border-radius: 18px; cursor: pointer; color: #fff; text-align: center;
          background: linear-gradient(160deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02));
          border: 1px solid rgba(255, 255, 255, 0.12);
          transition: transform 0.18s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .ctile:hover:not(:disabled) { transform: translateY(-4px); border-color: rgba(255, 255, 255, 0.28); box-shadow: 0 14px 36px rgba(0, 0, 0, 0.45); }
        .ctile:disabled { cursor: not-allowed; opacity: 0.7; }
        .ctile[draggable="true"] { cursor: grab; }
        .ctile[draggable="true"]:active { cursor: grabbing; }
        .ctile.is-dragging { opacity: 0.4; }
        .ctile.is-dragover { border-color: var(--accent-primary, #6366f1); box-shadow: 0 0 0 2px var(--accent-primary, #6366f1) inset; }
        .ctile_screen { position: relative; display: inline-flex; }
        .ctile_mon { width: 78px; height: auto; display: block; }
        .ctile_bezel { fill: #0c1020; stroke: rgba(255, 255, 255, 0.18); stroke-width: 1.5; }
        .ctile_stand, .ctile_base { fill: #4b5366; }
        .ctile_glass { fill: var(--scr, #4ade80); filter: drop-shadow(0 0 6px var(--scrglow, rgba(74, 222, 128, 0.6))); transition: fill 0.25s ease; }
        .ctile.is-inuse   { --scr: #16e07a; --scrglow: rgba(22, 224, 122, 0.85); }
        .ctile.is-locked  { --scr: #eab308; --scrglow: rgba(234, 179, 8, 0.6); }
        .ctile.is-offline { --scr: #f43f5e; --scrglow: rgba(244, 63, 94, 0.55); opacity: 0.85; }
        .ctile.is-inuse .ctile_glass { animation: ctilePulse 1.8s ease-in-out infinite; }
        @keyframes ctilePulse { 0%, 100% { filter: drop-shadow(0 0 5px var(--scrglow)); } 50% { filter: drop-shadow(0 0 12px var(--scrglow)); } }
        .ctile_ping {
          position: absolute; top: 2px; right: 6px; width: 10px; height: 10px; border-radius: 50%;
          background: var(--scr, #4ade80); box-shadow: 0 0 8px var(--scrglow, rgba(74, 222, 128, 0.7)); border: 2px solid #0c1020;
        }
        .ctile_name { font-size: 14px; font-weight: 700; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .ctile_meta { font-size: 12px; color: #aeb4c0; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      `}</style>
        </div>
    );
}
